import React from 'react';

import { Select } from '@aline/common';
import { makeStyles } from 'tss-react/mui';

import type { SuggestionStop } from './suggestion-model';
import { CURRENT_STOP_TONE, SUGGESTION_TONES } from './suggestion-tones';
import { SuggestionActions } from './SuggestionActions';
import { SuggestionRationaleTooltip } from './SuggestionRationaleTooltip';
import { useMoveHover, useStopDecision } from './SuggestionReviewContext';

const useStyles = makeStyles<{ current: boolean }>()((theme, { current }) => ({
  body: {
    color: theme.appColors.iconsType,
    flexGrow: 1,
    fontSize: 14,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  card: {
    alignItems: 'center',
    backgroundColor: current ? SUGGESTION_TONES.change.background : 'transparent',
    border: `1px dashed ${current ? CURRENT_STOP_TONE.color : SUGGESTION_TONES.change.color}`,
    borderRadius: 6,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  label: {
    color: SUGGESTION_TONES.change.color,
    flexShrink: 0,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
  },
}));

interface GhostCardProps {
  children: React.ReactNode;
  current: boolean;
  /** The move's stop. Its entity id is what the arrow overlay pairs this end with its source strip by. */
  stop: SuggestionStop;
}

/**
 * The destination end of a pending move: a dashed card in the target slot
 * with its own ✕ / ✓. It takes no number, so the real items keep their
 * count. Both ends resolve the same move.
 */
export const GhostCard = React.memo(function GhostCard({
  children,
  current,
  stop,
}: GhostCardProps): JSX.Element {
  const { classes } = useStyles({ current });
  const decision = useStopDecision(stop);
  const hover = useMoveHover(stop.entityId);

  return (
    <div
      className={classes.card}
      data-cy={Select.playbooks.suggestionGhost}
      data-move-anchor={`${stop.entityId}:ghost`}
      onClick={decision.onSelect}
      onMouseEnter={hover.onMouseEnter}
      onMouseLeave={hover.onMouseLeave}
    >
      <SuggestionRationaleTooltip rationales={[stop.rationale]}>
        <span className={classes.label}>Final position</span>
      </SuggestionRationaleTooltip>
      <span className={classes.body}>{children}</span>
      <SuggestionActions
        acceptDisabledReason={stop.blockedReason}
        acceptTitle="Accept move"
        rejectTitle="Reject move"
        size={14}
        onAccept={decision.onAccept}
        onReject={decision.onReject}
      />
    </div>
  );
});
